
import { Component, Prop } from 'vue-property-decorator';
import { FeedbackCategory } from '@/model/pleased';
import { sendToParent } from '@/common/bridge';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import * as PleasedApi from '@/common/api/api.pleased';
import * as Locale from '@/plugins/i18n';
import { analytics, syncLogs, COMPONENT_EVENTS } from '@/common/logs';
import { ChatRateMode } from '@/common/api/models.pleased';

const RATING_IMAGES = [
  { text: 'page.feedback.rating.unhappy', png: require('@/assets/img/rating/unhappy.png'), gif: require('@/assets/img/rating/unhappy.gif') },
  { text: 'page.feedback.rating.unsatisfied', png: require('@/assets/img/rating/unsatisfied.png'), gif: require('@/assets/img/rating/unsatisfied.gif') },
  { text: 'page.feedback.rating.neutral', png: require('@/assets/img/rating/neutral.png'), gif: require('@/assets/img/rating/neutral.gif') },
  { text: 'page.feedback.rating.satisfied', png: require('@/assets/img/rating/satisfied.png'), gif: require('@/assets/img/rating/satisfied.gif') },
  { text: 'page.feedback.rating.happy', png: require('@/assets/img/rating/happy.png'), gif: require('@/assets/img/rating/happy.gif') },
];

@Component
export default class ChatRating extends mixins(LogPageEventMixin) {
  $refs!: {
    index: HTMLDivElement
  }
  @Prop({ default: [] }) readonly feedbackCategories!: FeedbackCategory[];
  @Prop({ default: 'chat' }) readonly mode!: ChatRateMode;
  @Prop(String) readonly entityId!: string;
  @Prop({ type: String, default: '' }) readonly trackEventContext!: string;
  @Prop({ type: Boolean, default: false }) readonly canConnectAgent!: boolean;

  readonly RATING_IMAGES = RATING_IMAGES;
  readonly Locale = Locale;

  get [PAGE_TRACKING] () {
    return {
      name: `${this.trackEventContext}${PAGE_EVENTS.CSAT}`,
      selection: this.entityId,
      metadata: {
        type: this.mode,
        id: this.entityId,
        ...(this.mode === 'AI' ? { sessionId: analytics.sessionId } : {}),
      },
    };
  }

  selectedStar: number | null = 0; // TODO: Update to null in "New version"
  hoveredStar: number | null = null;

  selectedOptionIds: number[] = [];
  comment = '';
  submittedFeedback = false;

  get isTicket () {
    return this.mode === 'ticket';
  }

  get isChatMode () {
    return this.mode === 'chat' || this.mode === 'AI';
  }

  getImagePath (index: number) {
    const isSelected = this.selectedStar === index;
    const isHovered = this.hoveredStar === index;
    const isGifFormat = !this.selectedStar || isSelected || isHovered;

    const format = isGifFormat ? 'gif' : 'png';

    return this.RATING_IMAGES[index - 1][format];
  }

  created () {
    this.trackSeenRating();
  }

  handleMouseEnter (i: number) {
    this.hoveredStar = i;
  }

  handleMouseLeave () {
    this.hoveredStar = null;
  }

  handleSelectStar (i: number) {
    this.selectedStar = i;

    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_CLICKED}`,
      selection: this.entityId,
      metadata: { rating: i, type: this.mode },
    });

    this.$emit('send:feedback', {
      rating: this.selectedStar,
      comment: this.comment,
      feedbackSelectionIds: this.selectedStar < 4 ? this.selectedOptionIds : [],
      type: this.mode,
    });
  }

  toggleOption (id: number) {
    const index = this.selectedOptionIds.indexOf(id);

    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_FEEDBACK_OPTION}`,
      selection: this.entityId,
      metadata: {
        feedbackOption: id,
        title: this.feedbackCategories.find((f) => f.feedbackSelectionId === id)?.name,
        selected: index === -1,
        type: this.mode,
      },
    });

    if (index !== -1) {
      this.selectedOptionIds.splice(index, 1);
    } else {
      this.selectedOptionIds.push(id);
    }
  }

  async skip () {
    this.$gtag.event(GA_EVENT_NAME_ENUM.CHAT_RATING_SKIP);
    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_SKIP}`,
      selection: this.entityId,
      metadata: { type: this.mode },
    });

    await syncLogs();
    sendToParent('CLEAR_FLOAT');
    sendToParent('CLOSE');
  }

  handleSubmit () {
    this.$gtag.event(GA_EVENT_NAME_ENUM.CHAT_RATING_SUBMIT);

    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_SUBMITTED}`,
      selection: this.entityId,
      metadata: { type: this.mode },
    });

    const showConnectSupport = this.canConnectAgent && this.mode === 'AI' && Number(this.selectedStar) <= 2;

    this.$emit('submit', {
      rating: this.selectedStar,
      comment: this.comment,
      feedbackSelectionIds: this.selectedStar! < 4 ? this.selectedOptionIds : [],
      type: this.mode,
      showedConnectSupport: showConnectSupport,
    });

    // For AI CSAT we propose user to connect with support team if rating is less than 3
    if (showConnectSupport) {
      this.openConnectSupportModal();
    }

    this.submittedFeedback = true;
  }

  trackSeenRating () {
    // Do not track AI mode for now
    if (this.mode === 'AI') return;
    this.$gtag.event(GA_EVENT_NAME_ENUM.CHAT_RATING_SHOW);
    PleasedApi.trackEntitySeen({ entityType: this.mode, entityId: parseInt(this.entityId) });
  }

  openConnectSupportModal () {
    if (!this.canConnectAgent) return;

    analytics.track({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_LOW_RATING_SHOWN}`,
      selection: this.entityId,
      metadata: { type: this.mode },
    });

    const dialog = this.$dialog({
      content: this.$t('page.feedback.connect_support_dialog.text') as string,
      buttons: [
        {
          name: this.$t('global.yes'),
          callback: () => {
            analytics.trackClick({
              name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_LOW_RATING_CONNECT_SUPPORT}`,
              selection: this.entityId,
              metadata: { type: this.mode },
            });

            this.$emit('connect-support');
            dialog.close();
          },
        },
        {
          name: this.$t('global.no'),
          callback: async () => {
            analytics.trackClick({
              name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_LOW_RATING_CLOSE}`,
              selection: this.entityId,
              metadata: { type: this.mode },
            });

            await syncLogs();
            sendToParent('CLEAR_FLOAT');
            sendToParent('CLOSE');
          },
        },
      ],
    });
  }
}
